import { type ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { type MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import type {
  WaterCalendar,
  WaterCalendarClaim,
  WaterCalendarReservation,
  WaterCalendarRow,
  WaterCalendarStay,
} from '@models/water-calendar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { CrmWaterCalendarService } from '@services/crm-water-calendar.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { fromDateOnly, toDateOnly } from '@utils/date-only';
import { finalize } from 'rxjs';
import {
  addDays,
  type BlockedReason,
  datesBetween,
  type LaneCell,
  laneCells,
  layoutRows,
  monthWindow,
  packPeriodLanes,
  type RowLayout,
  stayOfClaim,
  stepMonth,
} from './water-calendar-grid';

/**
 * Availability tape chart for one lake (CS-218).
 *
 * Bookable items are rows and the days of the window are narrow columns of equal width. The columns are
 * only a ruler: everything else is a horizontal bar drawn over them, spanning from its first night to
 * its last. Because stays are ranges rather than per-day cells, the Saturday-to-Saturday changeover
 * rhythm and the gaps between bookings read at a glance.
 *
 * The two things shown come from different places and are deliberately kept apart. **Season periods**
 * (including closures) live in the legacy period tables, authored as ISO week + day with no year, and
 * occupy the top lane. **Occupancy** lives in `ItemOccupancies`, one row per claimed night per item, and
 * fills the item rows. A night can be both closed and booked; that is normal, and collapsing the two
 * into one state per day is the legacy bug that made "we closed this" indistinguishable from "someone
 * bought this".
 *
 * Read-only. Occupancy rows are derived state, re-projected from the reservation lines by a resync job
 * (docs/booking-per-day.md §1.2), so a night is changed by changing its reservation — which is what
 * clicking a bar goes off to do.
 */
@Component({
  selector: 'app-water-calendar',
  templateUrl: './water-calendar.component.html',
  styleUrl: './water-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    OverlayModule,
    RouterLink,
    TranslatePipe,
  ],
})
export class WaterCalendarComponent implements OnInit, OnDestroy {
  protected readonly codeListsService = inject(CodeListsService);
  private readonly calendarService = inject(CrmWaterCalendarService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** The picked lakes. Display order is alphabetical, not this order — see {@link selectedWaters}. */
  protected readonly selectedWaterIds = signal<number[]>([]);

  /** Any day inside the shown month. Normalised to the 1st whenever it is set from the picker. */
  protected readonly anchor = signal(new Date());

  /**
   * What has been read, per lake. Every selected lake is fetched independently and rendered as its own
   * band of the one table, so a lake that fails to load costs only its own rows: it keeps its header and
   * offers a retry there, rather than blanking the lakes that did load.
   *
   * Entries survive a month change and are re-fetched rather than cleared, which keeps the table's height
   * steady while the new month lands. Stale rows cannot show wrong dates: the layout is keyed by the
   * *current* window's dates, so a bar outside it is dropped rather than misplaced.
   */
  private readonly loads = signal<ReadonlyMap<number, LakeLoad>>(new Map());

  /**
   * Requests in flight, keyed by lake *and* window — see {@link syncLoads} for why the window matters.
   * Only ever asked whether it is empty, and whether one lake+window is already going.
   */
  private readonly pending = signal<ReadonlySet<string>>(new Set());

  /** One spinner in the toolbar covers the lot; individual bands do not need their own. */
  protected readonly isLoading = computed(() => this.pending().size > 0);

  // --- Water picker ---
  //
  // An autocomplete that only ever *adds*, with the selection as a chip row under the toolbar: chips
  // inside the field grow it and shove the month navigation around. The ~110 lakes arrive as one code
  // list, so filtering is local.

  /** What the user has typed. Only ever a search: the field never displays the selection. */
  private readonly waterQuery = signal('');

  /**
   * The chips. Derived from the ids rather than stored alongside them, which is what lets ids restored
   * from the URL turn into chips by themselves once the code list arrives. Ids the code list does not
   * know are dropped instead of shown as blanks — the chart still loads such an id, and its own header
   * names the lake.
   */
  protected readonly selectedWaters = computed(() => {
    const byId = new Map(this.codeListsService.waters().map((water) => [water.id, water]));
    return (
      this.selectedWaterIds()
        .map((id) => byId.get(id))
        .filter((water): water is NonNullable<typeof water> => water !== undefined)
        // Alphabetical, matching the bands of the table below, so the chips are a guide to it.
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  });

  /** Already-picked lakes are left out: they are in the field as chips, so offering them again is noise. */
  protected readonly filteredWaters = computed(() => {
    const picked = new Set(this.selectedWaterIds());
    const query = this.waterQuery().toLowerCase().trim();
    return this.codeListsService
      .waters()
      .filter((water) => !picked.has(water.id) && (!query || water.name.toLowerCase().includes(query)));
  });

  // --- The ruler ---

  private readonly window = computed(() => monthWindow(this.anchor()));

  protected readonly windowDates = computed(() => {
    const { from, to } = this.window();
    return datesBetween(from, to);
  });

  protected readonly dayCount = computed(() => this.windowDates().length);

  private readonly todayDate = toDateOnly(new Date());

  /**
   * One entry per day column: its date, the day of the month, and whether it is today. The template prints
   * a weekday initial from `value` as well, which is what makes the changeover rhythm readable.
   *
   * Sat+Sun are deliberately not *shaded*: that pair marks nothing the business has — the block families
   * arrive on Saturday, Monday and Friday (docs/booking-period-blocks.md §1), so a weekend tint would
   * highlight neither, and a flat grey band in a chart about availability just reads as unavailable. The
   * initial names every day equally and leaves the reader to spot the anchor that matters for the lake.
   */
  protected readonly dayColumns = computed(() => {
    const { from } = this.window();
    return this.windowDates().map((date, index) => {
      const value = addDays(from, index);
      return {
        date,
        value,
        dayOfMonth: value.getDate(),
        isToday: date === this.todayDate,
      };
    });
  });

  protected readonly windowStart = computed(() => this.window().from);

  // --- Bands ---

  /**
   * One band per selected lake, alphabetically, each laid out against the shared ruler: its season lane
   * (which doubles as the lake's header row) and its item rows.
   *
   * Every lake is in the same table so the day columns line up across all of them — comparing lakes on a
   * date is the whole point of picking more than one, and it only works with one ruler.
   */
  protected readonly bands = computed<LakeBand[]>(() => {
    const dates = this.windowDates();
    const dayCount = this.dayCount();
    const loads = this.loads();
    const reservations = this.reservationsById();
    const named = new Map(this.codeListsService.waters().map((water) => [water.id, water.name]));

    return this.selectedWaterIds()
      .map((waterId) => {
        const load = loads.get(waterId);
        const calendar = load?.calendar ?? null;
        return {
          waterId,
          // The response's own name is authoritative; the code list is the fallback while it loads, and
          // the id itself is the last resort for a lake the code list does not know.
          name: calendar?.waterName ?? named.get(waterId) ?? `#${waterId}`,
          // A load that came back with no calendar is a load that failed; `LakeLoad` does not carry that
          // twice.
          failed: load !== undefined && load.calendar === null,
          hasLoaded: load !== undefined,
          periodLanes: calendar
            ? packPeriodLanes(calendar.periods, dates).map((lane) => laneCells(lane, dayCount))
            : [],
          itemRows: calendar ? layoutRows(calendar.rows, calendar.periods, dates, reservations) : [],
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  /** Nothing has come back yet for any selected lake, so there is no table to draw at all. */
  protected readonly hasAnyLoad = computed(() => this.bands().some((band) => band.hasLoaded));

  // --- Reservation detail ---
  //
  // Claims carry only an id; the detail arrives once per reservation in the same response, so the hover
  // panel opens with no fetch and no spinner. Merged across lakes — reservation ids are unique, so one map
  // serves every band and the bar helpers stay lake-agnostic.

  private readonly reservationsById = computed(() => {
    const byId = new Map<number, WaterCalendarReservation>();
    for (const load of this.loads().values()) {
      for (const reservation of load.calendar?.reservations ?? []) byId.set(reservation.id, reservation);
    }
    return byId;
  });

  /**
   * The hovered claim's reservation, the stay it belongs to, and the reservation's other stays — which is
   * the point of the panel: a bar cannot say whether it is one item of a larger booking, and stays outside
   * the window are exactly the ones the chart cannot show.
   *
   * `null` when the reservation row has gone; the bar falls back to the stale marker.
   */
  protected summary(claim: WaterCalendarClaim): {
    reservation: WaterCalendarReservation;
    stay: WaterCalendarStay | null;
    otherStays: WaterCalendarStay[];
  } | null {
    const reservation = this.reservationsById().get(claim.reservationId);
    if (!reservation) return null;

    const stay = stayOfClaim(reservation, claim);
    return { reservation, stay, otherStays: reservation.stays.filter((s) => s !== stay) };
  }

  // --- Links out ---

  /**
   * `backUrl` for the reservation form, so editing a booking returns to this exact lake and month.
   * Built from the state rather than read off the router, which is not reactive.
   */
  protected readonly reservationLinkParams = computed(() => ({
    backUrl: `/availability?waterIds=${this.selectedWaterIds().join(',')}&date=${toDateOnly(this.anchor())}`,
  }));

  /** A lake's pricing screen, which is where its season periods are authored. */
  protected periodsEditLink(waterId: number): (string | number)[] {
    return ['/waters/edit', waterId, 'pricing'];
  }

  ngOnInit(): void {
    // Restore from the URL so a shared link opens on the same lakes, zoom and window.
    const params = this.route.snapshot.queryParamMap;

    const ids = parseWaterIds(params.get('waterIds'));
    if (ids.length > 0) this.selectedWaterIds.set(ids);

    const date = fromDateOnly(params.get('date'));
    if (date) this.anchor.set(date);

    this.syncLoads();
    this.syncUrl();
  }

  ngOnDestroy(): void {
    this.cancelClose();
  }

  // --- Water selection ---

  protected onWaterSearch(event: Event): void {
    this.waterQuery.set((event.target as HTMLInputElement).value);
  }

  /**
   * Leaving the field abandons the search. The chips are the selection, so there is nothing to restore
   * the text to — a half-typed query left sitting in the field would only read as an uncommitted lake.
   */
  protected onWaterBlur(input: HTMLInputElement): void {
    input.value = '';
    this.waterQuery.set('');
  }

  /**
   * The input is cleared by hand because the autocomplete writes the picked option's value into it
   * directly, outside Angular's binding — the same reason Material's own chips example does this.
   */
  protected addWater(waterId: number, input: HTMLInputElement): void {
    input.value = '';
    this.waterQuery.set('');
    if (this.selectedWaterIds().includes(waterId)) return;
    this.applySelection([...this.selectedWaterIds(), waterId]);
  }

  protected removeWater(waterId: number): void {
    this.applySelection(this.selectedWaterIds().filter((id) => id !== waterId));
  }

  protected clearWaters(): void {
    this.applySelection([]);
  }

  private applySelection(waterIds: number[]): void {
    this.selectedWaterIds.set(waterIds);
    this.syncUrl();
    this.syncLoads();
  }

  // --- Window navigation ---

  protected previousMonth(): void {
    this.goTo(stepMonth(this.anchor(), -1));
  }

  protected nextMonth(): void {
    this.goTo(stepMonth(this.anchor(), 1));
  }

  protected thisMonth(): void {
    this.goTo(new Date());
  }

  /**
   * The picker opens on its multi-year view, so a month is chosen as year → month and this fires before
   * the day grid is ever reached. Closing here is what makes it a month/year picker rather than a date
   * picker that happens to start zoomed out.
   */
  protected onMonthSelected(date: Date, picker: MatDatepicker<Date>): void {
    this.goTo(date);
    picker.close();
  }

  private goTo(date: Date): void {
    const { from, to } = this.window();
    // Already the month on screen — re-picking it should not refetch.
    if (date >= from && date <= to) return;

    this.anchor.set(date);
    this.syncUrl();
    // Each band's rows stay up while the new month lands — same lakes, same items — so the table does not
    // collapse and re-expand on every step. Bars cannot be stale: the layout is keyed by the new window's
    // dates, so anything outside it is dropped rather than drawn in the wrong column.
    this.syncLoads();
  }

  // --- Reservation summary panel ---

  /** Below the bar, flipping above it near the bottom of the viewport. */
  protected readonly summaryPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
  ];

  /**
   * The one open panel, as `reservationId:lineKind:lineId` — a line, since a row can hold several bars.
   *
   * The kind is part of the key because spot and accommodation lines are separate tables with their own
   * id sequences, so a reservation holding both can carry the same line id twice.
   */
  private readonly openSummaryKey = signal<string | null>(null);

  /**
   * Closing is delayed so the pointer can travel from the bar into the panel without it vanishing on the
   * way — the panel carries an email and a phone number, which are there to be selected and copied, so
   * it has to be reachable.
   */
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly CloseDelayMs = 150;

  private static summaryKey(claim: WaterCalendarClaim): string {
    return `${claim.reservationId}:${claim.lineKind}:${claim.reservationLineId}`;
  }

  protected isSummaryOpen(claim: WaterCalendarClaim): boolean {
    return this.openSummaryKey() === WaterCalendarComponent.summaryKey(claim);
  }

  protected openSummary(claim: WaterCalendarClaim): void {
    this.cancelClose();
    this.openSummaryKey.set(WaterCalendarComponent.summaryKey(claim));
  }

  protected scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.openSummaryKey.set(null), WaterCalendarComponent.CloseDelayMs);
  }

  protected cancelClose(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  // --- Cells ---

  /** Why a crossed-off track cell is unavailable. */
  protected blockedTooltip(reason: BlockedReason): string {
    return this.translate.instant(`availability.blocked.${reason}`);
  }

  /** Hover text for a season bar: the authored ISO week+day range and whether it is an exception. */
  protected periodTooltip(period: {
    periodTypeName: string;
    exceptionYear: number | null;
    fromWeek: number;
    fromDay: number;
    untilWeek: number;
    untilDay: number;
  }): string {
    const range = this.translate.instant('availability.tooltip.period-range', {
      fromWeek: period.fromWeek,
      fromDay: period.fromDay,
      untilWeek: period.untilWeek,
      untilDay: period.untilDay,
    });
    const scope =
      period.exceptionYear !== null
        ? this.translate.instant('availability.tooltip.exception', { year: period.exceptionYear })
        : this.translate.instant('availability.tooltip.recurring');
    return `${period.periodTypeName} · ${range} · ${scope}`;
  }

  protected notOfferedLabel(row: WaterCalendarRow): string {
    return this.translate.instant(`availability.not-offered.${row.notOfferedReason ?? 'inactive'}`);
  }

  // --- Loading ---

  /** Identifies the window a load belongs to, so a response for a month since navigated away is dropped. */
  private readonly windowKey = computed(() => {
    const dates = this.windowDates();
    return `${dates[0]}..${dates[dates.length - 1]}`;
  });

  /**
   * Fetch whatever the current selection and month need, and nothing else: a lake already read for this
   * window is left alone, so adding a chip costs one request rather than re-reading every lake.
   */
  private syncLoads(): void {
    const key = this.windowKey();
    const dates = this.windowDates();

    for (const waterId of this.selectedWaterIds()) {
      if (this.loads().get(waterId)?.windowKey === key) continue;
      // Keyed by lake *and* window, not lake alone: a request for the month we just left does not count as
      // covering this one, and skipping on it would strand the lake empty, since that response is
      // discarded when it lands.
      if (this.pending().has(pendingKey(waterId, key))) continue;
      this.fetch(waterId, key, dates[0], dates[dates.length - 1]);
    }

    // Forget lakes that are no longer picked, so re-picking one re-reads it rather than showing whatever
    // it held when it was dropped.
    const selected = new Set(this.selectedWaterIds());
    if ([...this.loads().keys()].some((waterId) => !selected.has(waterId))) {
      this.loads.update((loads) => new Map([...loads].filter(([waterId]) => selected.has(waterId))));
    }
  }

  private fetch(waterId: number, key: string, from: string, to: string): void {
    const inFlight = pendingKey(waterId, key);
    this.pending.update((pending) => new Set(pending).add(inFlight));

    this.calendarService
      .getCalendar(waterId, from, to)
      .pipe(
        // `null`, not an empty calendar: a failure is the absence of an answer, and adopting an empty one
        // for it would have this lake's band report the month as fully available. Per-lake, so one failure
        // costs one band.
        showGenericErrorToast(this.toastService, () => null, this.translate.instant('availability.load-failed')),
        finalize(() =>
          this.pending.update((pending) => {
            const next = new Set(pending);
            next.delete(inFlight);
            return next;
          }),
        ),
      )
      .subscribe((calendar) => {
        // Dropped if the month moved on, or the lake was deselected, while this was in flight.
        if (key !== this.windowKey() || !this.selectedWaterIds().includes(waterId)) return;
        this.loads.update((loads) => new Map(loads).set(waterId, { windowKey: key, calendar }));
      });
  }

  /** Bound to the retry a failed band offers. Nothing re-attempts a load on its own. */
  protected retryLake(waterId: number): void {
    this.loads.update((loads) => {
      const next = new Map(loads);
      next.delete(waterId);
      return next;
    });
    this.syncLoads();
  }

  private syncUrl(): void {
    const waterIds = this.selectedWaterIds();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        waterIds: waterIds.length > 0 ? waterIds.join(',') : null,
        date: toDateOnly(this.anchor()),
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}

/** Identifies one in-flight request: a lake read for a particular window. */
function pendingKey(waterId: number, windowKey: string): string {
  return `${waterId}:${windowKey}`;
}

/** What one lake's fetch produced. A null `calendar` *is* the failure — there is no separate flag. */
interface LakeLoad {
  /** The window it was read for, so a month change knows to re-fetch it. */
  windowKey: string;
  calendar: WaterCalendar | null;
}

/** One lake's band of the table: its header/season lane and its item rows, against the shared ruler. */
interface LakeBand {
  waterId: number;
  name: string;
  failed: boolean;
  hasLoaded: boolean;
  periodLanes: LaneCell[][];
  itemRows: RowLayout[];
}

/** Ids from `waterIds=1,2,3`, dropping junk and repeats. */
function parseWaterIds(value: string | null): number[] {
  if (!value) return [];
  const ids = value
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)];
}
